"use client";

import { useMessages } from "../../context/i18n";
import { common as enCommon } from "../../dictionaries/en/common";
import Link from "next/link";
import { FC, ReactNode, useState } from "react";
import MediaBlock from "../blocks/MediaBlock";
import Button from "../Button";
import Typography from "../Typography";

export type Persona = {
  tabLabel: string;
  h3: ReactNode;
  body: ReactNode;
  ctaLabel: string;
  ctaHref: string;
  videoSrc: string;
  videoAlt: string;
};

type PersonaGridProps = {
  sectionTitle: ReactNode;
  intro?: ReactNode;
  personas: Persona[];
};

// Tabbed on desktop / accordion on mobile. The mobile accordion uses
// native <details> so the section stays accessible and indexable
// without JS, matching the FaqBlock pattern already in the codebase.
const PersonaGrid: FC<PersonaGridProps> = ({
  sectionTitle,
  intro,
  personas,
}) => {
  const m = useMessages("common", enCommon);
  const [activeTab, setActiveTab] = useState(0);
  const active = personas[activeTab];

  return (
    <section className="max-w-[1500px] px-3 md:px-12 mx-auto my-32 md:my-48">
      <div className="text-center max-w-3xl mx-auto">
        <Typography variant="h2" component="h2">
          {sectionTitle}
        </Typography>
        {intro && (
          <Typography variant="body1" className="mt-6 text-slate-500">
            {intro}
          </Typography>
        )}
      </div>

      <div className="hidden md:block mt-16">
        <div
          role="tablist"
          aria-label={m.personasLabel}
          className="flex flex-wrap items-center justify-center gap-2"
        >
          {personas.map((persona, index) => (
            <button
              key={persona.tabLabel}
              type="button"
              role="tab"
              aria-selected={index === activeTab}
              onClick={() => setActiveTab(index)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                index === activeTab
                  ? "bg-slate-950 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {persona.tabLabel}
            </button>
          ))}
        </div>
        <div className="mt-12 grid grid-cols-2 gap-12 items-center">
          <div>
            <Typography variant="h3" component="h3">
              {active.h3}
            </Typography>
            <Typography
              variant="body1"
              className="mt-6 text-slate-500 leading-relaxed"
            >
              {active.body}
            </Typography>
            <div className="mt-8">
              <Link href={active.ctaHref}>
                <Button size="l">{active.ctaLabel}</Button>
              </Link>
            </div>
          </div>
          <div className="bg-slate-150 rounded-2xl pt-10 px-10">
            <MediaBlock
              key={active.tabLabel}
              data={{
                type: "video",
                src: active.videoSrc,
                alt: active.videoAlt,
              }}
              className="rounded-b-none rounded-t-lg mx-auto max-h-[480px]"
            />
          </div>
        </div>
      </div>

      <div className="md:hidden mt-12 flex flex-col gap-4">
        {personas.map((persona) => (
          <details
            key={persona.tabLabel}
            className="group rounded-2xl bg-slate-100 open:bg-slate-100"
          >
            <summary className="cursor-pointer list-none p-5 text-base font-medium text-slate-900">
              {persona.tabLabel}
            </summary>
            <div className="px-5 pb-5">
              <Typography variant="subtitle1" className="block">
                {persona.h3}
              </Typography>
              <Typography
                variant="body2"
                className="mt-3 text-slate-600 leading-relaxed"
              >
                {persona.body}
              </Typography>
              <div className="mt-6 bg-white rounded-xl overflow-hidden">
                <MediaBlock
                  data={{
                    type: "video",
                    src: persona.videoSrc,
                    alt: persona.videoAlt,
                  }}
                  className="w-full"
                />
              </div>
              <div className="mt-6">
                <Link href={persona.ctaHref}>
                  <Button size="m">{persona.ctaLabel}</Button>
                </Link>
              </div>
            </div>
          </details>
        ))}
      </div>
    </section>
  );
};

export default PersonaGrid;
